import { Helmet } from "react-helmet-async";
import { LANGS, type Lang, type Translations } from "../lib/translations";
import { useLang, useT } from "../lib/i18n";

/**
 * Single source of truth for the canonical domain. When a custom domain
 * changes (or we add `www.`), update here and rebuild. Everything canonical
 * / hreflang / OG computes from this.
 */
const SITE_URL = "https://minutapp.tech";

/** Maps runtime `lang` → the value to put on `<html lang="...">`. */
const HTML_LANG: Record<Lang, string> = {
  en: "en",
  pt: "pt-BR",
  es: "es",
};

/** Maps runtime `lang` → the value OG / Twitter tags expect. */
const OG_LOCALE: Record<Lang, string> = {
  en: "en_US",
  pt: "pt_BR",
  es: "es_ES",
};

const HREF_LANG: Record<Lang, string> = {
  en: "en",
  pt: "pt-BR",
  es: "es",
};

const DEFAULT_OG_IMAGE_PATH = "/og-image-v4.png";
const OG_IMAGE_TYPE = "image/png";
const OG_IMAGE_WIDTH = "1200";
const OG_IMAGE_HEIGHT = "630";
const OG_IMAGE_ALT = "Minuta product preview";

// Indexable route IDs. Every value here maps to a URL the site cares about
// ranking for — auth/billing/account routes are explicitly *not* in this
// set and get `noindex` instead. The blog post template literal supports
// every dynamic post slug at compile time without enumerating them.
export type IndexablePath =
  | "/"
  | "/launch"
  | "/roadmap"
  | "/pricing"
  | "/downloads"
  | "/ia-reuniao-ingles"
  | "/sem-bot-na-reuniao"
  | "/compare/otter-vs-minuta"
  | "/compare/fireflies-vs-minuta"
  | "/compare/granola-vs-minuta"
  | "/compare/zoom-ai-companion-vs-minuta"
  | "/compare/krisp-vs-minuta"
  | "/compare/tldv-vs-minuta"
  | "/compare/tactiq-vs-minuta"
  | "/use-cases/ai-meeting-copilot"
  | "/use-cases/sales-engineer-discovery-call"
  | "/use-cases/recruiting-screening-call"
  | "/use-cases/legal-deposition-meeting-copilot"
  | "/use-cases/customer-success-renewal-call"
  | "/use-cases/freelancer-discovery-call"
  | "/use-cases/tech-lead-one-on-one"
  | "/use-cases/zoom-realtime-translation"
  | "/use-cases/zoom-auto-meeting-notes"
  | "/use-cases/independent-tech-consultant-call"
  | "/use-cases/private-meeting-transcription"
  | "/use-cases/no-plugin-meeting-transcription"
  | "/blog"
  | `/blog/${string}`
  | "/privacy"
  | "/terms"
  | "/trust"
  | "/onde-seus-dados-vao"
  | "/donde-van-tus-datos";

/**
 * Schema.org types the site emits as JSON-LD. Pages that need structured
 * data declare which type(s) to render via the `schema` prop on `<Seo />`,
 * and pass any per-page payload (article frontmatter, breadcrumb trail) via
 * `schemaOptions`.
 */
export type SchemaKind =
  | "softwareApplication"
  | "faqPage"
  | "product"
  | "blogPosting"
  | "article"
  | "breadcrumbList";

/** Per-page payload required by `BlogPosting` / `Article` schemas. */
export interface ArticleSchemaOptions {
  /** Article headline. Defaults to the page `title` when omitted. */
  headline?: string;
  /** ISO-8601 publish date. */
  datePublished: string;
  /** Optional ISO-8601 update date. */
  dateModified?: string;
  /** Author display name. Defaults to "Minuta". */
  author?: string;
  /**
   * Image URL — absolute, or a path under `public/` (the canonical site URL
   * is prepended automatically). Falls back to the page `ogImage` and then
   * the site default.
   */
  image?: string;
}

/** A single breadcrumb step. `url` may be a site-relative path or absolute URL. */
export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface SchemaOptions {
  /** Required when `schema` includes `blogPosting` or `article`. */
  article?: ArticleSchemaOptions;
  /**
   * Ordered breadcrumb trail (parents first, current page last). Required
   * when `schema` includes `breadcrumbList`.
   */
  breadcrumbs?: BreadcrumbItem[];
}

interface SeoProps {
  /**
   * Pattern identifier. When provided, the component emits canonical +
   * hreflang alternates for that URL across the three languages.
   */
  path?: IndexablePath;

  /** When true, override robots with `noindex,nofollow` and skip OG tags. */
  noindex?: boolean;

  /** Localised page title. `<title>` receives `${title} · Minuta` suffix. */
  title: string;

  /** Localised meta description. */
  description: string;

  /**
   * Optional override for the Open Graph / Twitter card image. Defaults
   * to the site-wide `/og-image-v2.png`. Pass an absolute path under
   * `public/` (e.g. `/blog-covers/<slug>.png`) — the canonical site URL
   * is prepended automatically.
   */
  ogImage?: string;

  /**
   * Optional override for generated hreflang alternates. Defaults to all
   * supported languages.
   */
  alternateLangs?: Lang[];

  /**
   * Per-page JSON-LD schema. Pass a single kind or an array — the homepage
   * for example renders `softwareApplication` + `faqPage` + `product`.
   * Skipped on `noindex` pages.
   */
  schema?: SchemaKind | SchemaKind[];

  /** Extra payload required by some schema kinds (article, breadcrumbs). */
  schemaOptions?: SchemaOptions;
}

/**
 * `Seo` renders all the per-route head tags via react-helmet-async.
 * Centralised so every page guarantees:
 *
 *   - Correct `<html lang="...">` (Google reads this for query matching)
 *   - `<title>` + `<meta description>` per language
 *   - `<link rel="canonical">` (kills duplicate content risk when the site
 *     is reachable through the CloudFront default domain too)
 *   - `<link rel="alternate" hreflang="...">` for all three languages plus
 *     `x-default`, which routes non-localised queries to the PT-BR launch page
 *   - Open Graph + Twitter cards for social scrapers (WhatsApp, Twitter,
 *     LinkedIn, Slack — none of them run JS)
 *   - Noindex on auth/billing/account routes
 *   - Per-page JSON-LD schema (SoftwareApplication, FAQPage, Product,
 *     BlogPosting, Article, BreadcrumbList) so Google can surface rich
 *     results in SERP
 */
export function Seo({
  path,
  noindex = false,
  title,
  description,
  ogImage,
  alternateLangs,
  schema,
  schemaOptions,
}: SeoProps) {
  const lang = useLang();
  const t = useT();
  const suffixed = brandedTitle(title);
  const ogImageUrl = absUrl(ogImage ?? DEFAULT_OG_IMAGE_PATH);

  // `alternates` is a list of <link rel="alternate" hreflang="...">. During
  // the PT-BR launch push, `x-default` points at Portuguese so ambiguous
  // users see the active Rota A offer first.
  const resolvedAlternateLangs = alternateLangs ?? LANGS;
  const alternates = path
    ? resolvedAlternateLangs.map((l) => ({
        lang: HREF_LANG[l],
        href: buildUrl(l, path),
      }))
    : [];

  const canonical = path ? buildUrl(lang, path) : undefined;
  const schemaKinds = !noindex && schema ? toArray(schema) : [];
  const schemaPayloads = schemaKinds.map((kind) =>
    buildSchema(kind, {
      lang,
      t,
      title: suffixed,
      description,
      canonical,
      ogImageUrl,
      options: schemaOptions,
    }),
  );

  return (
    <Helmet>
      <html lang={HTML_LANG[lang]} />
      <title>{suffixed}</title>
      <meta name="description" content={description} />

      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow" />
      )}

      {alternates.map((a) => (
        <link key={a.lang} rel="alternate" hrefLang={a.lang} href={a.href} />
      ))}
      {path && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={buildUrl("pt", path)}
        />
      )}

      {/* Open Graph — skip on noindex pages so shares of, say, /billing/done
          (with a session_id in the URL) don't leak into previews. */}
      {!noindex && <meta property="og:type" content="website" />}
      {!noindex && <meta property="og:site_name" content="Minuta" />}
      {!noindex && <meta property="og:title" content={suffixed} />}
      {!noindex && <meta property="og:description" content={description} />}
      {!noindex && <meta property="og:locale" content={OG_LOCALE[lang]} />}
      {!noindex &&
        resolvedAlternateLangs.filter((l) => l !== lang).map((l) => (
          <meta key={l} property="og:locale:alternate" content={OG_LOCALE[l]} />
        ))}
      {!noindex && canonical && <meta property="og:url" content={canonical} />}
      {!noindex && <meta property="og:image" content={ogImageUrl} />}
      {!noindex && <meta property="og:image:secure_url" content={ogImageUrl} />}
      {!noindex && <meta property="og:image:type" content={OG_IMAGE_TYPE} />}
      {!noindex && <meta property="og:image:width" content={OG_IMAGE_WIDTH} />}
      {!noindex && <meta property="og:image:height" content={OG_IMAGE_HEIGHT} />}
      {!noindex && <meta property="og:image:alt" content={OG_IMAGE_ALT} />}
      {!noindex && <meta itemProp="thumbnailUrl" content={ogImageUrl} />}
      {!noindex && <link rel="image_src" href={ogImageUrl} />}
      {!noindex && <meta itemProp="image" content={ogImageUrl} />}

      {!noindex && <meta name="twitter:card" content="summary_large_image" />}
      {!noindex && <meta name="twitter:title" content={suffixed} />}
      {!noindex && <meta name="twitter:description" content={description} />}
      {!noindex && <meta name="twitter:image" content={ogImageUrl} />}

      {schemaPayloads.map((payload, i) => (
        <script key={schemaKinds[i]} type="application/ld+json">
          {JSON.stringify(payload)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Convenience wrapper: reads the active language's translations, pulls the
 * right title/description from `t.seo[page]`, and forwards to `<Seo />`.
 * Keeps call-sites to a single line with minimal ceremony.
 */
export function SeoForPage({ page, path, noindex, schema, schemaOptions }: {
  page: keyof ReturnType<typeof useT>["seo"];
  path?: IndexablePath;
  noindex?: boolean;
  schema?: SchemaKind | SchemaKind[];
  schemaOptions?: SchemaOptions;
}) {
  const t = useT();
  const block = t.seo[page];
  return (
    <Seo
      title={block.title}
      description={block.description}
      path={path}
      noindex={noindex}
      schema={schema}
      schemaOptions={schemaOptions}
    />
  );
}

// -----------------------------------------------------------------------------
//  Helpers
// -----------------------------------------------------------------------------

function buildUrl(lang: Lang, path: IndexablePath): string {
  const prefix = lang === "en" ? "" : `/${lang}`;
  // Ensure a trailing slash on the bare apex URL — matches what CloudFront
  // serves at `/` + helps dedupe `/` vs `<empty>` in Google's index.
  return `${SITE_URL}${prefix}${path === "/" ? "/" : path}`;
}

/** Resolve a possibly-relative URL against `SITE_URL`. */
function absUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`;
}

function toArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

function brandedTitle(title: string): string {
  return /\bMinuta\b/.test(title) ? title : `${title} · Minuta`;
}

interface SchemaContext {
  lang: Lang;
  t: Translations;
  title: string;
  description: string;
  canonical?: string;
  ogImageUrl: string;
  options?: SchemaOptions;
}

function buildSchema(kind: SchemaKind, ctx: SchemaContext): Record<string, unknown> {
  switch (kind) {
    case "softwareApplication":
      return softwareApplicationSchema(ctx.lang, ctx.description);
    case "faqPage":
      return faqSchema(ctx.t);
    case "product":
      return pricingSchema(ctx.lang, ctx.t, ctx.canonical);
    case "blogPosting":
      return articleSchema("BlogPosting", ctx);
    case "article":
      return articleSchema("Article", ctx);
    case "breadcrumbList":
      return breadcrumbListSchema(ctx);
  }
}

function softwareApplicationSchema(lang: Lang, description: string) {
  const featureList: Record<Lang, string[]> = {
    en: [
      "Live AI copilot drafts answers during English, Portuguese, and Spanish meetings",
      "Detects questions aimed at you in the live transcript",
      "Suggested answers grounded in your documents and meeting context",
      "Real-time translation across English, Portuguese, and Spanish",
      "Live meeting transcription on your computer",
      "Works with Zoom, Microsoft Teams, Google Meet — no bot joins",
      "Automatic meeting summary and action items",
      "Runs locally on macOS and Windows",
    ],
    pt: [
      "Copiloto de IA ao vivo sugere respostas durante reuniões em inglês, português e espanhol",
      "Detecta perguntas direcionadas a você na transcrição ao vivo",
      "Respostas sugeridas com base nos seus documentos e no contexto da reunião",
      "Tradução em tempo real entre inglês, português e espanhol",
      "Transcrição ao vivo da reunião no seu computador",
      "Funciona com Zoom, Teams, Google Meet — sem bot na call",
      "Ata automática com resumo e action items",
      "Roda localmente no macOS e Windows",
    ],
    es: [
      "Copiloto de IA en vivo sugiere respuestas durante reuniones en inglés, portugués y español",
      "Detecta preguntas dirigidas a ti en la transcripción en vivo",
      "Respuestas sugeridas basadas en tus documentos y el contexto de la reunión",
      "Traducción en tiempo real entre inglés, portugués y español",
      "Transcripción en vivo en tu computadora",
      "Funciona con Zoom, Teams, Google Meet — sin bots",
      "Acta automática con resumen y action items",
      "Corre localmente en macOS y Windows",
    ],
  };

  const keywords: Record<Lang, string> = {
    en: "AI meeting copilot, live meeting copilot, multilingual meeting copilot, English Portuguese Spanish meetings, sales engineer copilot, technical calls, what to say in meetings",
    pt: "copiloto de reunião, copiloto multilíngue para calls, IA para responder em reunião, reunião em inglês português espanhol, sales engineer, call técnica",
    es: "copiloto de reuniones, copiloto multilingüe para calls, IA para responder en reuniones, reuniones en inglés portugués español, sales engineer, call técnica",
  };

  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Minuta",
    description,
    applicationCategory: "BusinessApplication",
    operatingSystem: "macOS 13+; Windows 10 21H2+",
    inLanguage: HTML_LANG[lang],
    featureList: featureList[lang],
    keywords: keywords[lang],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
    },
    url: buildUrl(lang, "/"),
    image: `${SITE_URL}/og-image-v2.png`,
  };
}

function faqSchema(t: Translations) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: t.faq.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

function pricingSchema(lang: Lang, t: Translations, canonical?: string) {
  // Pricing copy lives under `t.pricing`. Sourcing the schema fields from
  // there means the JSON-LD stays in sync with the on-page copy without a
  // second translation surface to maintain.
  //
  // When the page passes `path="/pricing"`, the schema points at the
  // dedicated route. Otherwise (e.g. emitted from the homepage where the
  // pricing section lives at `#pricing`) we fall back to the localised
  // landing URL with the in-page anchor.
  const pricingUrl = canonical ?? `${buildUrl(lang, "/")}#pricing`;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Minuta",
    description: t.pricing.description,
    brand: {
      "@type": "Brand",
      name: "Minuta",
    },
    url: pricingUrl,
    image: `${SITE_URL}/og-image-v2.png`,
    offers: [
      {
        "@type": "Offer",
        name: t.pricing.free.label,
        description: t.pricing.free.tagline,
        price: "0",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: pricingUrl,
      },
      {
        "@type": "Offer",
        name: t.pricing.pro.label,
        description: t.pricing.pro.tagline,
        price: "9",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: pricingUrl,
      },
    ],
  };
}

function articleSchema(
  type: "BlogPosting" | "Article",
  ctx: SchemaContext,
): Record<string, unknown> {
  const article = ctx.options?.article;
  if (!article) {
    throw new Error(
      `<Seo schema="${type === "BlogPosting" ? "blogPosting" : "article"}" /> requires schemaOptions.article`,
    );
  }

  const image = absUrl(article.image ?? ctx.ogImageUrl);
  const author = article.author ?? "Minuta";

  const payload: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": type,
    headline: article.headline ?? ctx.title,
    description: ctx.description,
    image,
    datePublished: article.datePublished,
    inLanguage: HTML_LANG[ctx.lang],
    author: {
      "@type": "Organization",
      name: author,
    },
    publisher: {
      "@type": "Organization",
      name: "Minuta",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/og-image-v2.png`,
      },
    },
  };
  if (article.dateModified) payload.dateModified = article.dateModified;
  if (ctx.canonical) {
    payload.mainEntityOfPage = {
      "@type": "WebPage",
      "@id": ctx.canonical,
    };
    payload.url = ctx.canonical;
  }
  return payload;
}

function breadcrumbListSchema(ctx: SchemaContext): Record<string, unknown> {
  const breadcrumbs = ctx.options?.breadcrumbs;
  if (!breadcrumbs || breadcrumbs.length === 0) {
    throw new Error(
      '<Seo schema="breadcrumbList" /> requires schemaOptions.breadcrumbs',
    );
  }
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: absUrl(crumb.url),
    })),
  };
}
